import type { ThemeTokens } from "@klarity/themes";
import { Field, TextInput } from "../../../components/ui/Field";
import type { FontInfo } from "../../../lib/api/models";
import { FontPicker } from "./FontPicker";
import { LogoPicker } from "./LogoPicker";
import { getAt, setAt, TOKEN_FIELDS, type TokenField } from "./token-fields";

const GROUPS = [...new Set(TOKEN_FIELDS.map((f) => f.group))];
const unit = (f: TokenField) => (f.kind === "pt" ? " (pt)" : f.kind === "mm" ? " (mm)" : "");

export function TokenForm({
  tokens,
  onChange,
  errors,
  fonts,
  onUploadFont,
  onUploadLogo,
  readOnly = false,
}: {
  tokens: ThemeTokens;
  onChange: (next: ThemeTokens) => void;
  errors: Record<string, string>;
  fonts: FontInfo[];
  onUploadFont: (file: File) => void;
  onUploadLogo: (which: "cover" | "header", file: File) => void;
  readOnly?: boolean;
}) {
  const set = (path: string, value: unknown) => onChange(setAt(tokens, path, value));
  const control = (f: TokenField) => {
    const v = getAt(tokens, f.path);
    const label = `${f.label}${unit(f)}`;
    if (f.kind === "bool") {
      return (
        <label key={f.path} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={readOnly}
            checked={v === true}
            onChange={(e) => set(f.path, e.target.checked)}
          />
          {f.label}
        </label>
      );
    }
    return (
      <Field key={f.path} label={label} error={errors[f.path]}>
        {(id, describedBy) =>
          f.kind === "color" ? (
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label={`${f.label} picker`}
                disabled={readOnly}
                value={/^#[0-9a-f]{6}$/i.test(String(v)) ? String(v) : "#000000"}
                onChange={(e) => set(f.path, e.target.value.toUpperCase())}
                className="h-9 w-10 cursor-pointer rounded border border-line bg-surface p-0.5"
              />
              <TextInput
                id={id}
                aria-describedby={describedBy}
                invalid={Boolean(errors[f.path])}
                readOnly={readOnly}
                className="font-mono"
                value={String(v ?? "")}
                onChange={(e) => set(f.path, e.target.value)}
              />
            </div>
          ) : f.kind === "text" ? (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              invalid={Boolean(errors[f.path])}
              readOnly={readOnly}
              value={String(v ?? "")}
              onChange={(e) => set(f.path, e.target.value)}
            />
          ) : (
            <TextInput
              id={id}
              type="number"
              step="0.5"
              min="0"
              aria-describedby={describedBy}
              invalid={Boolean(errors[f.path])}
              readOnly={readOnly}
              value={typeof v === "number" ? String(v) : ""}
              onChange={(e) => set(f.path, e.target.value === "" ? Number.NaN : Number(e.target.value))}
            />
          )
        }
      </Field>
    );
  };

  return (
    <div className="grid gap-6">
      <section className="grid gap-3 sm:grid-cols-2">
        <FontPicker
          label="Body font"
          value={tokens.fonts.body}
          fonts={fonts}
          error={errors["fonts.body.family"]}
          readOnly={readOnly}
          onUpload={onUploadFont}
          onChange={(next) => set("fonts.body", next)}
        />
        <FontPicker
          label="Mono font"
          value={tokens.fonts.mono}
          fonts={fonts}
          error={errors["fonts.mono.family"]}
          readOnly={readOnly}
          onUpload={onUploadFont}
          onChange={(next) => set("fonts.mono", next)}
        />
        <LogoPicker
          label="Cover logo"
          value={tokens.logos.cover}
          readOnly={readOnly}
          onUpload={(file) => onUploadLogo("cover", file)}
          onChange={(src) => set("logos.cover", src)}
        />
        <LogoPicker
          label="Header logo"
          value={tokens.logos.header}
          readOnly={readOnly}
          onUpload={(file) => onUploadLogo("header", file)}
          onChange={(src) => set("logos.header", src)}
        />
      </section>
      {GROUPS.map((g) => (
        <fieldset key={g} className="grid min-w-0 gap-3 rounded-xl border border-line bg-surface p-5">
          <legend className="px-1 text-sm font-semibold text-ink">{g}</legend>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {TOKEN_FIELDS.filter((f) => f.group === g).map(control)}
          </div>
        </fieldset>
      ))}
    </div>
  );
}
